import { lazy, Suspense } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Route, Routes, useLocation } from 'react-router';
import { Header, Footer } from '@ui/siteStructure';
import { QueryClient, QueryClientProvider, HydrationBoundary, DehydratedState } from '@tanstack/react-query';
import { CmsPage } from './pages/CmsPage';
// SC-1213: the client renders from the lazy, code-split registry — unchanged behaviour,
// now supplied explicitly instead of imported inside Row.
import { cmsWebComponentsMap } from '@ui/webComponentExports';
import { isSocialGraphicPath, SocialRoutes } from './pages/SocialPage/SocialRoutes';
import { useScrollToTop } from './utils/useScrollToTop';
import { ScrollToTop } from '@ui/common/components/ScrollToTop/ScrollToTop';
import { useThemeQuery } from '@data/settings/useThemeQuery';
import { useTenantConfigQuery } from '@data/tenants';
import { useTheme } from './utils/useTheme';
import 'react-loading-skeleton/dist/skeleton.css';
import { useGlobalScrollEvents } from './utils/useGlobalScrollEvents';
import { GlobalContextProvider, useGlobalContext } from '@slc/tools/GlobalContext';
import { getEnvLabel, getNodeEnv } from '@constants';
import { Loader } from '@ui/common/components/Loader/Loader';

import { SidebarAds } from '@ui/ads/components/SidebarAds';
import { InterstitialAdGuard } from '@ui/ads/components/InterstitialAdGuard';

// Lazy-load non-critical below-fold components to reduce initial JS bundle
const AccessibilityWidget = lazy(() =>
  import('@ui/common/components/AccessibilityWidget').then((m) => ({ default: m.AccessibilityWidget }))
);
const TenantSwitcher = lazy(() => import('./components/TenantSwitcher').then((m) => ({ default: m.TenantSwitcher })));

// Route-level code splitting — only CmsPage is statically imported (handles homepage)
const ArticlePage = lazy(() => import('./pages/ArticlePage').then((m) => ({ default: m.ArticlePage })));
const FixturesPage = lazy(() => import('./pages/FixturesPage').then((m) => ({ default: m.FixturesPage })));
const ResultsPage = lazy(() => import('./pages/ResultsPage').then((m) => ({ default: m.ResultsPage })));
const TeamPage = lazy(() => import('./pages/TeamPage').then((m) => ({ default: m.TeamPage })));
// MatchPageWithLiveUpdates wraps MatchPage in SocketProvider for live match-detail updates.
const MatchPage = lazy(() =>
  import('./pages/MatchPage/MatchPage').then((m) => ({ default: m.MatchPageWithLiveUpdates }))
);
const MatchCentrePage = lazy(() =>
  import('./pages/MatchCentre/MatchCentre').then((m) => ({ default: m.MatchCentrePage }))
);
const ClubPage = lazy(() => import('./pages/ClubPage').then((m) => ({ default: m.ClubPage })));
const PlayerPage = lazy(() => import('./pages/PlayerPage').then((m) => ({ default: m.PlayerPage })));
const PhotoAlbumPage = lazy(() => import('./pages/PhotoAlbumPage').then((m) => ({ default: m.PhotoAlbumPage })));
const EngagementHubPage = lazy(() =>
  import('./pages/EngagementHubPage').then((m) => ({ default: m.EngagementHubPage }))
);
const EngagementCampaignPage = lazy(() =>
  import('./pages/EngagementCampaignPage').then((m) => ({ default: m.EngagementCampaignPage }))
);
const NewsletterConfirm = lazy(() =>
  import('./pages/NewsletterConfirm').then((m) => ({ default: m.NewsletterConfirm }))
);
const SocialFixturesPage = lazy(() => import('./pages/SocialPage').then((m) => ({ default: m.SocialFixturesPage })));
const SocialTablePage = lazy(() => import('./pages/SocialPage').then((m) => ({ default: m.SocialTablePage })));
const SocialDraftPage = lazy(() => import('./pages/SocialPage').then((m) => ({ default: m.SocialDraftPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: QueryClient;
    __REACT_QUERY_STATE__?: DehydratedState;
  }
}

function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { data: theme } = useThemeQuery();
  useTheme(theme);
  return <>{children}</>;
}

function AppContent() {
  const ctx = useGlobalContext();
  const envLabel = getEnvLabel();
  const isDevMode = getNodeEnv() !== 'production';
  const location = useLocation();

  useScrollToTop();

  // const location = useLocation();
  // HACK for removing the SM AI ads script from /live page and adding it back for rest of the pages
  // useEffect(() => {
  //   // Check current route and manage script accordingly
  //   const manageScript = () => {
  //     const existingScript = document.getElementById('sm-ai-script');

  //     if (location.pathname === '/live') {
  //       // On /live page - remove the script
  //       if (existingScript) {
  //         existingScript.remove();
  //       }
  //     } else {
  //       // Not on /live page - add the script if it doesn't exist
  //       if (!existingScript) {
  //         // Use setTimeout to ensure script is added after navigation completes
  //         setTimeout(() => {
  //           const script = document.createElement('script');
  //           script.id = 'sm-ai-script';
  //           script.src = `https://cdn.browsiprod.com/bootstrap/bootstrap.js?t=${Date.now()}`;
  //           script.setAttribute('data-pubKey', 'SnackMedia');
  //           script.setAttribute('data-siteKey', 'd_mapping');

  //           // Force reload by removing async
  //           script.async = false;

  //           // Append to body instead of head for better execution
  //           document.body.appendChild(script);

  //           // Force a reflow to trigger script execution
  //           void document.body.offsetHeight;
  //         }, 100);
  //       }
  //     }
  //   };

  //   // Run immediately to handle initial state
  //   manageScript();
  // }, [location.pathname]);

  // Subscribe to url listeners for anchor hash changes that trigger smooth scrolling
  useGlobalScrollEvents();

  // Social graphics are screenshotted as images, so they render without the site chrome
  // (header, footer, ads, widgets) around them.
  if (isSocialGraphicPath(location.pathname)) {
    return (
      <ThemeProvider>
        <Suspense fallback={<Loader />}>
          <SocialRoutes
            fixtures={<SocialFixturesPage />}
            table={<SocialTablePage />}
            draft={(template) => <SocialDraftPage template={template} />}
          />
        </Suspense>
      </ThemeProvider>
    );
  }

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>{ctx.siteName}</title>
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:locale" content="en_GB" />
        <meta property="og:site_name" content={ctx.siteName} />
        {ctx.twitterSite && <meta name="twitter:site" content={ctx.twitterSite} />}
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <ThemeProvider>
        <div className="page-wrapper">
          <Header environment={envLabel ?? undefined} />
          <div className="main-content-wrapper">
            <main id="main-content">
              <Suspense fallback={<Loader />}>
                <Routes>
                  <Route path="/match/:id" element={<MatchPage />} />
                  <Route path="/clubs/:slug" element={<ClubPage />} />
                  <Route path="/players/:id" element={<PlayerPage />} />
                  <Route path="/teams/:slug" element={<TeamPage />} />
                  <Route path="/articles/:slug" element={<ArticlePage />} />
                  <Route path="/photo-albums/:id" element={<PhotoAlbumPage />} />
                  <Route path="/engagement-hub" element={<EngagementHubPage />} />
                  <Route path="/engagement/:id" element={<EngagementCampaignPage />} />
                  <Route path="/results" element={<ResultsPage />} />
                  <Route path="/fixtures" element={<FixturesPage />} />
                  <Route path="/live" element={<MatchCentrePage />} />
                  <Route path="/newsletter-confirm" element={<NewsletterConfirm />} />
                  <Route path="*" element={<CmsPage componentsMap={cmsWebComponentsMap} />} />
                </Routes>
              </Suspense>
              <ScrollToTop />
            </main>
            <SidebarAds />
            {/* Drops the GPT interstitial behind the page when it renders with an empty body,
                so an unfilled full-screen overlay can't swallow every click. */}
            <InterstitialAdGuard />
            <div className="primis-video-ad" id="snack-vod" />
          </div>
          <Footer />
          <Suspense fallback={null}>
            <AccessibilityWidget />
          </Suspense>
          {isDevMode && (
            <Suspense fallback={null}>
              <TenantSwitcher />
            </Suspense>
          )}
        </div>
      </ThemeProvider>
    </>
  );
}

function ConnectedGlobalContextProvider({ children }: { children: React.ReactNode }) {
  const { data: tenantConfig, isLoading } = useTenantConfigQuery();
  return (
    <GlobalContextProvider tenantConfig={tenantConfig} isLoading={isLoading}>
      {children}
    </GlobalContextProvider>
  );
}

export function ClientApp() {
  if (!import.meta.env.PROD && typeof window !== 'undefined') {
    window.__TANSTACK_QUERY_CLIENT__ = queryClient;
  }

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <HydrationBoundary state={typeof window !== 'undefined' ? window.__REACT_QUERY_STATE__ : undefined}>
          <ConnectedGlobalContextProvider>
            <AppContent />
          </ConnectedGlobalContextProvider>
        </HydrationBoundary>
      </QueryClientProvider>
    </HelmetProvider>
  );
}
